/**
 * Builds a new instance of the Carousel component.
 * @module Carousel
 * @constructor
 */

/** @lends module:Carousel */
function Carousel (scope, util, element, params) {
    var that = this;
    that.attribute = 'data-stry-carousel';

    /*  Catch instantiation errors  */
    if (!scope && !util && !element && !params) { return; }  //  Registration condition
    else {
        if (!scope) { console.error('StoryStream -> Carousel -> No scope provided.'); return; }
        if (!util) { console.error('StoryStream -> Carousel -> No util available.'); return; }
        if (!element) { console.error('StoryStream -> Carousel -> No element provided.'); return; }
        if (!params) { console.error('StoryStream -> Carousel -> No params provided.'); return; }
    }
    /*  End  */

    that.elem = element;
    that.isStatic = false;
    that.items = [];
    that.items_order = {};
    that.items_step = {
        back: false,
        forward: false
    };
    that.items_total = params.stryNumberOfItems;
    that.uid = params.uid;


    /**
     * Funnel all events through this method and distribute.
     * @memberof Carousel
     * @function handleEvent
     * @param {object} e - Event data.
     * @inner
     */
    var handleEvent = function(e, params) {
        var type = e.type ? e.type : (e.html ? 'renderer' : false);
        switch(type) {
            case 'viewport.click':

                //  Listen for Carousel related actions
                if (e.detail.action === 'click.carousel') {
                    var target = e.detail.e.target;
                    var target_key = parseFloat(target.getAttribute('data-stry-key'));

                    //  If element key matches those in scope for this instance of the component
                    if (that.uid === target_key) {
                        var target_index = parseFloat(target.getAttribute('data-stry-index'));


                        //  Each image is an anchor link to the relevant case study page.
                        //  When an image is clicked that isn't the current active/focussed item
                        //  we need to prevent the default link out to allow it to move into focus.
                        if (that.items_order.active !== target_index) {

                            //  Standard prevention
                            e.detail.e.preventDefault();

                            //  Trigger the carousel movement
                            whenNewCarouselItemIsSelected(target_index);
                        }
                    }
                }

                break;
            case 'viewport.change':

                //  Any actions necessary after view change
                whenViewportHasChanged();

                break;
            case 'transitionend':

                //  Remove listener once complete
                e.target.removeEventListener('transitionend', handleEvent);

                //  Remove animation class once complete
                util.removeClass(that.elem, 'animate');

                break;
        }
    };


    /**
     * Function used construct functionality for Carousel.
     * @memberof Carousel
     * @function willConstructComponent
     * @inner
     */
    var willConstructComponent = function () {
        var media_items = that.elem.getElementsByClassName('media-item');
        var quote_items = that.elem.getElementsByClassName('quote-item');
        var media_link_items = that.elem.getElementsByClassName('media-item-link');
        var new_active_item;
        var len = params.stryNumberOfItems;
        var index = 0;

        //  Generate a reference for each media item
        while (len--) {

            //  Add new object to house media
            that.items.push({});

            //  Add media element
            that.items[index].media = media_items[index];

            //  Increment index to maintian order fidelity
            index += 1;
        }

        //  Generate a reference for each quote item
        len = quote_items.length;
        index = 0;
        while (len--) {

            //  Add quote element
            that.items[index].quote = quote_items[index];

            //  Increment index to maintian order fidelity
            index += 1;
        }

        //  Add unique instance key to all clickable elements to maintain scope
        len = media_link_items.length;
        index = 0;
        while (len--) {

            //  Add key to elements
            media_link_items[index].setAttribute('data-stry-key', that.uid);

            //  Increment index to maintian order fidelity
            index += 1;
        }

        //  Randomly select an item from carousel to feature first
        new_active_item = Math.floor(Math.random() * (params.stryNumberOfItems - 1)) + 1;

        //  Set new order of items based of the active item
        that.items_order = willConfigureItemIndexOrderAndReturn(new_active_item);

        //  Position elements accordingly (without animating)
        willAdjustPositionOfCarouselItems(that.items_order);

        //  Show the first quote element
        util.addClass(that.items[new_active_item - 1].quote, 'quote-active');
    };


    /**
     * Function for moving an item along in the sequence.
     * @memberof Carousel
     * @function willSwitchCarouselItems
     * @param {number} target_index - The index number of the item to be focused.
     * @inner
     */
    var willSwitchCarouselItems = function (target_index) {
        var new_items_order = willConfigureItemIndexOrderAndReturn(target_index);

        //  Add class so the switch will be animated
        util.addClass(that.elem, 'animate');

        //  Move the items to their new positions based on the new order object
        willAdjustPositionOfCarouselItems(new_items_order);

        //  Show corresponding quote
        willTransitionQuoteItems(target_index);

        //  Update the current state with the new order
        that.items_order = new_items_order;
    };


    /**
     * Function used to control the visibility of the case study quotes.
     * @memberof Carousel
     * @function willSwitchCarouselItems
     * @param {number} target_index - The index number of the item to be focused.
     * @inner
     */
    var willTransitionQuoteItems = function (target_index) {
        var quote_items = that.elem.getElementsByClassName('quote-item');
        var len = quote_items.length;

        //  Reset all quote elements
        while (len--) {

            quote_items[len].className = 'quote-item';
        }

        //  Show the relevant quote element
        util.addClass(that.items[target_index - 1].quote, 'quote-active');
    };


    /**
     * Function used to position items off screen ready for animation.
     * @memberof Carousel
     * @function willPositionOffScreenItemBasedOnStepDirection
     * @inner
     */
    var willPositionOffScreenItemBasedOnStepDirection = function () {
        var new_ready_index;

        //  Forward (right)
        if (that.items_step.forward) {

            //  Within range, just increment the index
            if (that.items_order.after < that.items_total) {
                new_ready_index = that.items_order.after + 1;
            }

            //  At the end of range set index to first item
            else {
                new_ready_index = 1;
            }

            //  Position the item ready for animation
            that.items[new_ready_index - 1].media.className = 'media-item media-after-ready';
        }

        //  Backward (left)
        else if (that.items_step.back) {

            //  Within range, just reduce the index by one
            if (that.items_order.before > 1) {
                new_ready_index = that.items_order.before - 1;
            }

            //  At the start of range set index to last item
            else {

                new_ready_index = that.items_total;
            }

            //  Position the item ready for animation
            that.items[new_ready_index - 1].media.className = 'media-item media-before-ready';
        }
    };


    /**
     * Function to move carousel items to their new positions.
     * @memberof Carousel
     * @function willAdjustPositionOfCarouselItems
     * @param {object} new_items_order - New set of item positions.
     * @inner
     */
    var willAdjustPositionOfCarouselItems = function (new_items_order) {
        var media_items = that.elem.getElementsByClassName('media-item');
        var len = media_items.length;

        //  Reset class names and remove any listeners
        while (len--) {

            media_items[len].className = 'media-item';
            media_items[len].removeEventListener('transitionend', handleEvent);
        }

        //  Position onscreen items accoring to new order
        util.addClass(that.items[new_items_order.active - 1].media, 'media-active');
        util.addClass(that.items[new_items_order.before - 1].media, 'media-before');
        util.addClass(that.items[new_items_order.after - 1].media, 'media-after');

        //  Exit the previous 'before' (on the left) item as the 3 new items are moved across
        if (that.items_step.forward) {
            that.items[that.items_order.before - 1].media.className = 'media-item media-before media-before-exit';
        }

        //  Exit the previous 'after' (on the right) item as the 3 new items are moved across
        else if (that.items_step.back) {
            that.items[that.items_order.after - 1].media.className = 'media-item media-after media-after-exit';
        }

        //  Add listener to active item to determin when animation is complete
        that.items[new_items_order.active - 1].media.addEventListener('transitionend', handleEvent);
    };


    /**
     * Function to handle logic around order of carousel items, returns an object of order based on index provided.
     * @memberof Carousel
     * @function willConfigureItemIndexOrderAndReturn
     * @param {number} index - The index number of the item to be focused.
     * @inner
     */
    var willConfigureItemIndexOrderAndReturn = function (index) {
        var before_index;
        var after_index;

        //  Focus is an item in the mid range
        if (index > 1 && index < that.items_total) {

            before_index = index - 1;
            after_index = index + 1;
        }

        //  Focus is initial item
        else if (index === 1) {

            before_index = that.items_total;
            after_index = index + 1;
        }

        //  Focus is last item
        else if (index === that.items_total) {

            before_index = index - 1;
            after_index = 1;
        }

        return {
            active: index,
            after: after_index,
            before: before_index
        };
    };


    /**
     * Function to process user selection of valid carousel item.
     * @memberof Carousel
     * @function whenNewCarouselItemIsSelected
     * @param {number} target_index - The index number of the selected item.
     * @inner
     */
    var whenNewCarouselItemIsSelected = function (target_index) {

        //  Remove the animation class while we position the elements ready to animate
        util.removeClass(that.elem, 'animate');

        //  Logic to determine direction of movement
        var target_is_increment = target_index > that.items_order.active;
        var last_item_to_first = that.items_order.active === that.items_total && target_index === 1;
        var first_item_to_last = target_index === that.items_total && that.items_order.active === 1;

        //  Set the step direction for this movement
        that.items_step.back = (!target_is_increment || first_item_to_last) && !last_item_to_first;
        that.items_step.forward = (target_is_increment && !first_item_to_last) || last_item_to_first;

        /*
         *  Here we need to position an item of screen ready to animate in,
         *  this is dependant on the step direction.
         */
        willPositionOffScreenItemBasedOnStepDirection();

        //  Run content switch after render is ready
        util.repaint.run(willSwitchCarouselItems, target_index);
    };


    /**
     * Function used adjust the component based on viewport.
     * @memberof Carousel
     * @function whenViewportHasChanged
     * @inner
     */
    var whenViewportHasChanged = function () {
        var media_elem = that.elem.getElementsByClassName('module-carousel-media')[0];
        var media_item = media_elem.getElementsByClassName('media-item')[0];

        //  Set media element height
        media_elem.style.height = (media_item.offsetHeight / 10) + 'rem';
    };


    /**
     * Function used to initialise the application.
     * @memberof Carousel
     * @function init
     * @inner
     */
    var init = function () {

        //  Determine if this is static, ie only 1 item in the carousel
        if (params.stryNumberOfItems && params.stryNumberOfItems > 1) {
            that.isStatic = false;
        }
        else {
            that.isStatic = true;
        }

        //  Setup component
        if (!that.isStatic) {

            //  Setup the compnent instance
            willConstructComponent();

            //  Ensure elements have been adjusted based on viewport
            whenViewportHasChanged();

            //  Listen for viewport changes and update
            scope.addEventListener('viewport.change', handleEvent);
        }

        //  Listeners
        scope.addEventListener('viewport.click', handleEvent);
    };


    init();
    return that;
}
if (typeof module !== 'undefined' && exports !== null) {
    exports.Carousel = Carousel;
}
